<script>
import ClientDisabledBanner from "./components/global/client_disabled_banner.vue";
import GlobalModals from "./components/global/globalModals/global_modals.vue";
import FormView from "./views/form_view.vue";
import Sidebar from "./components/global/sidebar/sidebar.vue";
import Navbar from "./components/global/navbar.vue";
import PausedBanner from "./components/global/paused_banner.vue";
import { getUser } from "@/api";
import { forms } from "@/init/openForm";
import { initCrisp } from "@/init/crisp";
import { initCanny } from "@/init/canny";
import UserProfile from "./components/UserProfile.vue";
import {
  mergeUserData,
  showQuizModalIfNeeded,
  updateUserUnsafeMetadata,
} from "@/utils/userUtils";
import WorkspacesProvider from "@/components/global/workspaces_provider.vue";
import AccountProvider from "@/components/global/account-provider.vue";
import initOnboardings from "./init/onboarding/onboardings";
import { onboardingContext } from "./init/onboarding/onboardingContext";
import { setupHotjar } from "@/init/hotjar";
import MaterialsPlatformsProvider from "@/components/source-materials/materials-platforms-provider.vue";
import MaintenanceBanner from "@/components/global/maintenance_banner";
import TrialWelcomeModal from "@/components/global/modals/trial_welcome_modal.vue";
import TrialEndedModal from "@/components/global/modals/trial_ended_modal.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    MaterialsPlatformsProvider,
    AccountProvider,
    ClientDisabledBanner,
    MaintenanceBanner,
    Sidebar,
    Navbar,
    PausedBanner,
    GlobalModals,
    FormView,
    UserProfile,
    WorkspacesProvider,
  },

  inject: ["growthBook"],

  provide() {
    return {
      scrollContentToTop: this.scrollContentToTop,
    };
  },

  data() {
    return {
      forms,
      isNestedDropdownVisible: false,
      webClientDisabled: false,
      webClientDisabledMessage: undefined,
    };
  },

  computed: {
    ...mapGetters("config", ["features"]),
    formsLength() {
      return Object.keys(this.forms).length;
    },

    hideNavbar() {
      return this.$route?.meta?.hideNavbar;
    },
  },

  watch: {
    $route() {
      if (this.$refs.scrollerView) {
        this.$refs.scrollerView.scrollTop = 0;
      }
    },
  },

  beforeMount() {
    this.growthBook?.setAttributes({
      email: this.$store.state.user?.email || null,
    });
    this.webClientDisabledMessage = this.growthBook?.getFeatureValue(
      "web-client-disabled-message"
    );
    this.webClientDisabled = !!this.webClientDisabledMessage;

    this.initNestedDropdowns();
    this.$store.dispatch("config/initConfig");
    onboardingContext.createGrade =
      this.$store.getters["config/features"]?.createGrade || [];

    this.$eventBus.$on("onboarding:completed", this.scrollContentToTop);
  },
  mounted() {
    onboardingContext.workspacesWrapper =
      this.$refs.sidebar?.$refs.workspacesWrapper;
  },

  beforeDestroy() {
    this.$eventBus.$off("onboarding:completed", this.scrollContentToTop);
  },

  async created() {
    const clerk = this.$getClerk();
    const clerkUser = clerk.user;

    if (clerkUser) {
      await this.switchToDefaultOrganization(clerk, clerkUser);

      const { data } = await getUser("me");
      const userData = mergeUserData(this, data);
      await this.$store.dispatch("setUser", userData);
      await this.$store.dispatch("setClerkOrgContext", {
        ...this.$store.state.clerkOrgContext,
        userId: userData.id,
      });
      this.$store.dispatch("languages/load");

      initCrisp(userData);
      initCanny(userData);
      setupHotjar(userData);

      const quizModalShown = await showQuizModalIfNeeded(userData);

      // reload user since trial trigger might have happened after quiz completion
      if (quizModalShown) {
        const { data } = await getUser("me");
        const userData = mergeUserData(this, data);
        await this.$store.dispatch("setUser", userData);
      }

      await this.showTrialModals();

      if (this.features.showOnboardingTour) {
        await initOnboardings({ router: this.$router });
      }
    }
  },

  methods: {
    async switchToDefaultOrganization(clerk, clerkUser) {
      const memberships = await clerkUser.getOrganizationMemberships();

      let orgId = null;
      let clerkUserId = clerkUser.id;
      let isOrgAdmin = false;

      // users going to have just one org; switch to it automatically
      if (memberships.data.length > 0) {
        const membership = memberships.data[0];
        orgId = membership.organization.id;
        clerkUserId = membership.publicUserData.userId;
        isOrgAdmin = membership.role === "org:admin";

        // Only call setActive if org is not already active
        if (!clerk.organization || clerk.organization.id !== orgId) {
          try {
            await clerk.setActive({ organization: orgId });
          } catch (err) {
            console.error("Failed to switch organization context:", err);
          }
        }
      }

      await this.$store.dispatch("setClerkOrgContext", {
        orgId,
        clerkUserId,
        userId: null,
        isOrgAdmin,
      });
    },

    scrollContentToTop() {
      this.$refs.scrollerView?.scrollTo({ top: 0, behavior: "smooth" });
    },

    initNestedDropdowns() {
      this.$root.$on("bv::dropdown::show", (bvEvent) => {
        const parentDropdownName = bvEvent?.vueTarget?.$parent?.$options?.name;

        if (
          parentDropdownName === "EgDropdown" ||
          parentDropdownName === "BFormDatepicker"
        ) {
          this.isNestedDropdownVisible = true;
        }
      });

      this.$root.$on("bv::dropdown::hide", (bvEvent) => {
        const parentDropdownName = bvEvent?.vueTarget?.$parent?.$options?.name;

        if (
          parentDropdownName === "EgDropdown" ||
          parentDropdownName === "BFormDatepicker"
        ) {
          this.isNestedDropdownVisible = false;
        }

        if (this.isNestedDropdownVisible) {
          bvEvent.preventDefault();
        }
      });
    },

    async showTrialModals() {
      const { isTrialExpired, isTrial } = this.$store.getters;
      const user = this.$store.state.user;
      const hasSubscription = user?.stripeCustomerId !== null;

      // Priority 1: Show trial ended modal if trial expired within last 3 days and no subscription
      if (!hasSubscription && !user.hasSeenTrialEnded && isTrialExpired) {
        await this.$egModal.show("trial-ended-modal", {
          component: TrialEndedModal,
        });
        // Mark as seen
        await updateUserUnsafeMetadata({ hasSeenTrialEnded: true });
        return;
      }

      // Priority 2: Show welcome modal if user is in active trial and hasn't seen it
      if (isTrial && !user.hasSeenTrialWelcome) {
        await this.$egModal.show("trial-welcome-modal", {
          component: TrialWelcomeModal,
          on: {
            onSuccess: async () =>
              updateUserUnsafeMetadata({ hasSeenTrialWelcome: true }),
          },
        });
      }
    },
  },
};
</script>
<template>
  <div id="app">
    <div v-if="webClientDisabled">
      <client-disabled-banner
        :client-disabled-message="webClientDisabledMessage"
      />
    </div>
    <div v-else>
      <account-provider>
        <workspaces-provider>
          <materials-platforms-provider>
            <template #default="{ loadingFolders }">
              <maintenance-banner class="w-100" />
              <div class="wrapper">
                <div class="sidebar">
                  <sidebar v-if="!hideNavbar" ref="sidebar" />
                </div>
                <div class="main-content">
                  <navbar
                    v-if="!hideNavbar"
                    :show-onboarding-tour="features.showOnboardingTour"
                  />
                  <paused-banner />
                  <user-profile />
                  <form-view v-show="formsLength > 0" key="form-view" />
                  <div
                    v-show="formsLength === 0"
                    ref="scrollerView"
                    :class="[
                      'content',
                      { 'content--no-padding': $route.meta.noPadding },
                    ]"
                  >
                    <eg-b-overlay
                      v-if="$route.meta.requiresAuth && loadingFolders"
                      :show="true"
                      no-wrap
                    >
                      <template #overlay>
                        <eg-b-spinner small variant="info" />
                      </template>
                    </eg-b-overlay>
                    <router-view v-else key="router-view" />
                  </div>
                </div>
              </div>
              <global-modals />
            </template>
          </materials-platforms-provider>
        </workspaces-provider>
      </account-provider>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  width: 100%;

  .sidebar {
    max-width: 264px;
    z-index: 5;
    border-right: 1px solid var(--color-stroke-neutral-default);
  }

  .main-content {
    background-color: hsl(210, 29%, 97%);
    width: 100%;
    height: calc(100vh - var(--maintenance-height));
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .content {
    padding: 24px;
    height: 100%;
    width: 100%;
    display: flex;
    overflow: auto;

    & > * {
      width: 100%;
    }

    &--no-padding {
      padding: 0;
    }
  }
}
</style>
